// Pestaña "Decisiones" del asistente (/assistant): cerebro operativo, etapa 1.
// Las propuestas del cerebro (respuestas a preguntas y mensajes a compradores) esperan acá el
// veredicto del dueño: Sí / Sí con cambios / No. El cerebro nunca envía nada: el servidor ejecuta
// solo tras el sí, y antes verifica en vivo que siga abierta. Solo el Administrador (el servidor
// lo exige además del permiso). Sin dinero en pantalla.
import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../../api/client';
import { fetchResult, failureText, sellerName } from './brainView';
import {
  APPROVE_FAIL,
  APPROVE_UNKNOWN,
  REJECT_FAIL,
  confirmClickAllowed,
  editedTextToSend,
  REJECT_OPTIONS,
  agoText,
  approveOutcome,
  supersededText,
  unverifiedText,
  editProblem,
  evidenceTitle,
  mailSideLines,
  headerNote,
  kindLabel,
  nextDecisions,
  PRODUCT_UNCONFIRMED,
  previousQa,
  productUnconfirmed,
  situationLabel,
  countryOf,
  COUNTRY_UNKNOWN,
  statsLine,
  INVOICE_CARD_NOTE,
} from './decisionsView';
import { fmtShortDateTime } from './shared';
import { outcomeCell, outcomeLabel, type OutcomeRow } from './rulesView';

interface DecisionRow {
  id: number;
  seller_id: number;
  seller_name: string;
  kind: string;
  situation: string | null;
  text: string;
  preview: string;
  context: Record<string, unknown> | null;
  evidence: Record<string, unknown> | null;
  created_at: string;
  matter_id: number | null;
  max_len: number;
}
interface StatRow {
  kind: string;
  situation: string | null;
  proposed: number;
  approved_as_is: number;
  approved_edited: number;
  /** Aprobadas cuyo envío falló (no cuentan como aprobadas). */
  failed?: number;
  rejected: number;
  superseded: number;
  pending: number;
  avg_minutes_to_verdict: number | null;
}
interface Decisions {
  pending: { rows: DecisionRow[]; total: number };
  counts: {
    total: number;
    by_seller: Array<{
      seller_id: number;
      name: string;
      answer_question: number;
      pack_send_message: number;
      send_email?: number;
      reply_email?: number;
      send_invoice?: number;
    }>;
  };
  stats_7d: StatRow[];
  stats_30d: StatRow[];
  sellers: Array<{ id: number; name: string }>;
}
interface ApproveResult {
  executed: boolean;
  detail: string;
  superseded?: boolean;
  still_pending?: boolean;
}
/** Aviso que queda arriba de la lista cuando una tarjeta se va. */
interface Notice {
  id: number;
  ok: boolean;
  text: string;
}

const REFRESH_MS = 60_000;
/** Vence sola la confirmación del "Sí". */
const CONFIRM_MS = 10_000;
const KIND_FILTER: Array<{ value: string; label: string }> = [
  { value: '', label: 'Todas' },
  { value: 'answer_question', label: 'Preguntas' },
  { value: 'pack_send_message', label: 'Mensajes' },
  { value: 'send_email', label: 'Correos' },
  { value: 'reply_email', label: 'Respuestas en el hilo' },
  { value: 'send_invoice', label: 'Facturas' },
];

const fmt = (n: number) => Number(n).toLocaleString('es-AR');
const plural = (n: number, one: string, many: string) => `${fmt(n)} ${n === 1 ? one : many}`;
const minutes = (m: number | null) =>
  m == null ? '—' : m < 60 ? `${Math.round(m)} min` : `${Math.round(m / 60)} h`;
const str = (v: unknown): string | null => (typeof v === 'string' && v.trim() ? v : null);

export default function DecisionsTab({ onChanged }: { onChanged?: () => void }) {
  // null = la lista todavía no se pudo leer nunca (no es lo mismo que "no hay propuestas").
  const [data, setData] = useState<Decisions | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  const [seller, setSeller] = useState('');
  const [kind, setKind] = useState('');
  const [notices, setNotices] = useState<Notice[]>([]);
  // Tarjetas que se fueron en esta pantalla (para no esperar al próximo refresco).
  const [gone, setGone] = useState<Set<number>>(new Set());
  const dataRef = useRef<Decisions | null>(null);
  const seq = useRef(0);
  // Cuántas tarjetas están en edición o ejecutando: mientras haya alguna no se auto-refresca.
  const editing = useRef(new Set<number>());
  const noticeSeq = useRef(0);
  // Propuestas con un pedido en curso: nunca salen dos pedidos por la misma, aunque React tarde
  // en redibujar la tarjeta bloqueada.
  const inFlight = useRef(new Set<number>());
  // Resultados controlados a los 7 días (columna "Terminó bien"); null = sin dato o no se pudo leer.
  const [outcomes, setOutcomes] = useState<OutcomeRow[] | null>(null);

  const loadOutcomes = useCallback(async () => {
    const f = await fetchResult(() =>
      api.get<{ outcomes: OutcomeRow[] }>('/assistant/outcomes/summary?days=7')
    );
    // Si falla se conserva lo último bueno (la columna muestra "—" si nunca se pudo leer).
    if (f.kind === 'ok') setOutcomes(Array.isArray(f.data.outcomes) ? f.data.outcomes : []);
  }, []);

  useEffect(() => {
    void loadOutcomes();
    const t = setInterval(() => void loadOutcomes(), REFRESH_MS);
    return () => clearInterval(t);
  }, [loadOutcomes]);

  const load = useCallback(async () => {
    const my = ++seq.current;
    const q = new URLSearchParams({ limit: '100' });
    if (seller) q.set('seller_id', seller);
    if (kind) q.set('kind', kind);
    const f = await fetchResult(() => api.get<Decisions>(`/assistant/decisions?${q.toString()}`));
    if (my !== seq.current) return;
    const n = nextDecisions(dataRef.current, f);
    dataRef.current = n.value;
    setData(n.value);
    setWarning(n.warning);
    if (f.kind === 'ok') setGone(new Set());
  }, [seller, kind]);

  useEffect(() => {
    void load();
    const t = setInterval(() => {
      if (editing.current.size === 0) void load();
    }, REFRESH_MS);
    return () => clearInterval(t);
  }, [load]);

  const setEditing = useCallback((id: number, on: boolean) => {
    if (on) editing.current.add(id);
    else editing.current.delete(id);
  }, []);

  /** La tarjeta se fue (decidida): aviso arriba, contador de la pestaña y lista al día. */
  const finished = useCallback(
    (id: number, ok: boolean, text: string) => {
      editing.current.delete(id);
      setGone((g) => new Set(g).add(id));
      const nid = ++noticeSeq.current;
      setNotices((ns) => [{ id: nid, ok, text }, ...ns].slice(0, 5));
      onChanged?.();
      void load();
    },
    [load, onChanged]
  );

  const rows = (data?.pending.rows ?? []).filter((r) => !gone.has(r.id));

  return (
    <div className="space-y-6 p-4">
      <section className="rounded border border-gray-200 bg-white p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-base font-semibold">
              Propuestas que esperan tu decisión: {data ? fmt(data.counts.total) : '—'}
            </h2>
            <p className="text-sm text-gray-600">{headerNote(kind)}</p>
            {data && data.counts.by_seller.length > 0 && (
              <ul className="mt-1 text-sm text-gray-700">
                {data.counts.by_seller.map((b) => (
                  <li key={b.seller_id}>
                    {b.name || sellerName(data.sellers, b.seller_id)}:{' '}
                    {plural(b.answer_question, 'pregunta', 'preguntas')} ·{' '}
                    {plural(b.pack_send_message, 'mensaje', 'mensajes')}
                    {b.send_email ? ` · ${plural(b.send_email, 'correo', 'correos')}` : ''}
                    {b.reply_email ? ` · ${plural(b.reply_email, 'respuesta', 'respuestas')}` : ''}
                    {b.send_invoice ? ` · ${plural(b.send_invoice, 'factura', 'facturas')}` : ''}
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <label className="flex items-center gap-1">
              Empresa
              <select
                value={seller}
                onChange={(e) => setSeller(e.target.value)}
                className="rounded border border-gray-300 px-2 py-1"
              >
                <option value="">Todas</option>
                {(data?.sellers ?? []).map((s) => (
                  <option key={s.id} value={String(s.id)}>
                    {sellerName(data?.sellers, s.id)}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex items-center gap-1">
              Tipo
              <select
                value={kind}
                onChange={(e) => setKind(e.target.value)}
                className="rounded border border-gray-300 px-2 py-1"
              >
                {KIND_FILTER.map((k) => (
                  <option key={k.value} value={k.value}>
                    {k.label}
                  </option>
                ))}
              </select>
            </label>
            <button
              onClick={() => void load()}
              className="rounded border border-gray-300 px-3 py-1 hover:bg-gray-50"
            >
              Actualizar
            </button>
          </div>
        </div>
      </section>

      {warning && (
        <div className="rounded border border-amber-200 bg-amber-50 p-2 text-sm text-amber-800">
          {warning}
        </div>
      )}

      {data && <Measurement data={data} outcomes={outcomes} />}

      <section className="space-y-3">
        {notices.map((n) => (
          <div
            key={n.id}
            className={`rounded border p-2 text-sm ${n.ok ? 'border-emerald-200 bg-emerald-50 text-emerald-800' : 'border-red-200 bg-red-50 text-red-700'}`}
          >
            {n.text}
          </div>
        ))}
        {data && rows.length === 0 && !warning && (
          <p className="text-sm text-gray-500">No hay propuestas esperando tu decisión.</p>
        )}
        {data && data.pending.total > data.pending.rows.length && (
          <p className="text-xs text-gray-500">
            Se muestran las {fmt(data.pending.rows.length)} más viejas de{' '}
            {fmt(data.pending.total)}.
          </p>
        )}
        {rows.map((r) => (
          <Card
            key={r.id}
            row={r}
            sellerLabel={r.seller_name || sellerName(data?.sellers, r.seller_id)}
            setEditing={setEditing}
            finished={finished}
            inFlight={inFlight.current}
            refresh={() => void load()}
          />
        ))}
      </section>
    </div>
  );
}

function Measurement({ data, outcomes }: { data: Decisions; outcomes: OutcomeRow[] | null }) {
  return (
    <section className="rounded border border-gray-200 bg-white p-4">
      <h3 className="mb-2 text-sm font-semibold">Cómo se decidieron las propuestas</h3>
      <p className="text-sm">
        <b>Últimos 7 días:</b> {statsLine(data.stats_7d)}
      </p>
      <p className="text-sm">
        <b>Últimos 30 días:</b> {statsLine(data.stats_30d)}
      </p>
      {data.stats_30d.length > 0 && (
        <div className="mt-3 overflow-x-auto">
          <p className="mb-1 text-xs text-gray-500">Últimos 30 días, por tipo y situación</p>
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-500">
                {[
                  'Tipo',
                  'Situación',
                  'Propuestas',
                  'Tal cual',
                  'Con cambios',
                  'Aprobadas que fallaron',
                  'Rechazadas',
                  'Por otra vía o vencidas',
                  'Tiempo medio hasta tu decisión',
                  'Terminó bien (7 d)',
                ].map((h) => (
                  <th key={h} className="px-2 py-1 font-medium">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.stats_30d.map((s, i) => (
                <tr key={i} className="border-t border-gray-100">
                  <td className="px-2 py-1">{kindLabel(s.kind)}</td>
                  <td className="px-2 py-1">{situationLabel(s.situation)}</td>
                  <td className="px-2 py-1">{fmt(s.proposed)}</td>
                  <td className="px-2 py-1">{fmt(s.approved_as_is)}</td>
                  <td className="px-2 py-1">{fmt(s.approved_edited)}</td>
                  <td className="px-2 py-1">{fmt(s.failed ?? 0)}</td>
                  <td className="px-2 py-1">{fmt(s.rejected)}</td>
                  <td className="px-2 py-1">{fmt(s.superseded)}</td>
                  <td className="px-2 py-1">{minutes(s.avg_minutes_to_verdict)}</td>
                  <td className="px-2 py-1" title={outcomeTitle(outcomes, s.kind, s.situation)}>
                    {outcomeCell(outcomes, s.kind, s.situation)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/** Detalle de la celda "Terminó bien": cuántas de cada resultado en el control de 7 días. */
function outcomeTitle(rows: OutcomeRow[] | null, kind: string, situation: string | null): string {
  const mine = (rows ?? []).filter(
    (r) => r.check === '7d' && r.kind === kind && (r.situation ?? null) === (situation ?? null)
  );
  if (mine.length === 0) return 'Todavía no hay controles a los 7 días.';
  const sum = (k: 'bien' | 'neutro' | 'mal' | 'sin_dato') =>
    mine.reduce((a, r) => a + (Number(r[k]) || 0), 0);
  return (['bien', 'neutro', 'mal', 'sin_dato'] as const)
    .map((k) => `${outcomeLabel(k)}: ${fmt(sum(k))}`)
    .join(' · ');
}

type Mode = 'idle' | 'confirm' | 'edit' | 'edit_confirm' | 'reject' | 'busy';

function Card({
  row,
  sellerLabel,
  setEditing,
  finished,
  inFlight,
  refresh,
}: {
  row: DecisionRow;
  sellerLabel: string;
  setEditing: (id: number, on: boolean) => void;
  finished: (id: number, ok: boolean, text: string) => void;
  inFlight: Set<number>;
  refresh: () => void;
}) {
  const [mode, setModeState] = useState<Mode>('idle');
  const [draft, setDraft] = useState(row.text);
  const [error, setError] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Hora en que se armó la confirmación: el segundo clic de un doble clic no la dispara.
  const armedAt = useRef(0);

  const setMode = (m: Mode) => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    setModeState(m);
    // Mientras se edita, se confirma o se ejecuta, la lista no se refresca sola.
    setEditing(row.id, m !== 'idle');
  };

  // Al desmontar: limpiar el temporizador y la marca de edición.
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
      setEditing(row.id, false);
    },
    [row.id, setEditing]
  );

  /** Primer clic del "Sí": pide confirmar; vence sola a los 10 s. */
  const askConfirm = (m: 'confirm' | 'edit_confirm', back: Mode) => {
    armedAt.current = Date.now();
    setMode(m);
    timer.current = setTimeout(() => {
      timer.current = null;
      setModeState(back);
      setEditing(row.id, back !== 'idle');
    }, CONFIRM_MS);
  };

  const head = `${sellerLabel} · ${kindLabel(row.kind)}`;

  /** Clic en "Sí, enviar": ignorado si llega pegado al clic que armó la confirmación. */
  const confirmed = (send: () => void) => {
    if (!confirmClickAllowed(armedAt.current, Date.now())) return;
    send();
  };

  /** "Sí" (sin texto) o "Sí con cambios" (`edited` = texto del cuadro). */
  const approve = async (edited?: string) => {
    if (inFlight.has(row.id)) return;
    inFlight.add(row.id);
    // Editado igual al propuesto → se manda sin texto: cuenta como "tal cual".
    const finalText = edited === undefined ? undefined : editedTextToSend(edited, row.text);
    const back: Mode = edited === undefined ? 'idle' : 'edit';
    setMode('busy');
    setError(null);
    try {
      const r = await fetchResult(() =>
        api.post<ApproveResult>(
          `/assistant/decisions/${row.id}/approve`,
          finalText === undefined ? {} : { final_text: finalText }
        )
      );
      if (r.kind !== 'ok') {
        // Un texto conocido del servidor dice qué pasó; si no (red caída, error genérico), el
        // envío pudo haberse hecho: no se invita a repetir, se relee la lista.
        const known = failureText(r, '');
        if (known && known !== 'No se pudo aprobar la propuesta.') setError(known);
        else {
          setError(APPROVE_UNKNOWN);
          refresh();
        }
        setMode(back);
        return;
      }
      const o = approveOutcome(r.data);
      if (o === 'done') finished(row.id, true, `✓ Enviado — ${head}. ${r.data.detail}`);
      else if (o === 'superseded') finished(row.id, true, supersededText(head, r.data.detail));
      else if (o === 'unverified') {
        setError(unverifiedText(r.data.detail));
        setMode(back);
      } else if (o === 'kept') {
        // No se envió y la propuesta sigue pendiente (p. ej. el texto editado no pasa las
        // reglas): la tarjeta queda con el motivo y la edición conservada.
        setError(r.data.detail || APPROVE_FAIL);
        setMode(back);
      } else finished(row.id, false, `✗ No se pudo: ${r.data.detail} — ${head}`);
    } finally {
      inFlight.delete(row.id);
    }
  };

  const reject = async (reason: string) => {
    if (inFlight.has(row.id)) return;
    inFlight.add(row.id);
    setMode('busy');
    setError(null);
    let r;
    try {
      r = await fetchResult(() =>
        api.post<{ ok: true }>(`/assistant/decisions/${row.id}/reject`, { reason })
      );
    } finally {
      inFlight.delete(row.id);
    }
    if (r.kind !== 'ok') {
      setError(failureText(r, REJECT_FAIL));
      setMode('reject');
      return;
    }
    const why = REJECT_OPTIONS.find((o) => o.value === reason)?.label ?? 'Otro motivo';
    finished(row.id, true, `Rechazada — ${head} (${why}). No se envió nada.`);
  };

  const busy = mode === 'busy';
  const problem = mode === 'edit' || mode === 'edit_confirm' ? editProblem(draft, row.max_len) : null;
  const ctx = row.context ?? {};
  const thread = previousQa(ctx);
  const country = countryOf(ctx);
  // Correo a Mercado Libre (volver a reclamar no entregadas): no hay comprador, país ni producto.
  const isMail = row.kind === 'send_email' || row.kind === 'reply_email';
  // Mandar la factura: el texto es fijo de la empresa, no hay "Sí con cambios".
  const isInvoice = row.kind === 'send_invoice';

  return (
    <article
      className={`rounded border border-gray-200 bg-white p-4 ${busy ? 'pointer-events-none opacity-60' : ''}`}
      aria-busy={busy}
    >
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="font-medium">{sellerLabel}</span>
        {!isMail && (
          <>
            <span className="text-gray-400">·</span>
            <span
              className="rounded border border-amber-300 bg-amber-50 px-2 py-0.5 text-sm font-semibold"
              title="País del comprador: la respuesta puede cambiar según el país"
            >
              {country ? `${country.flag} ${country.name}`.trim() : COUNTRY_UNKNOWN}
            </span>
          </>
        )}
        <span className="text-gray-400">·</span>
        <span>{kindLabel(row.kind)}</span>
        <span className="text-gray-400">·</span>
        <span className="rounded bg-gray-100 px-1.5 py-0.5 text-xs">
          {situationLabel(row.situation)}
        </span>
        <span className="ml-auto text-xs text-gray-500">{agoText(row.created_at)}</span>
      </div>

      <Block title={isMail ? 'El correo' : 'Lo que escribió el comprador'}>
        <BuyerSide kind={row.kind} ctx={ctx} />
      </Block>

      {row.kind === 'answer_question' && thread.length > 0 && (
        <Block title="Lo que este comprador preguntó antes">
          <PreviousThread entries={thread} />
        </Block>
      )}

      {!isMail && (
        <Block title="Producto">
          <Product ctx={ctx} />
          {row.kind === 'answer_question' && productUnconfirmed(ctx) && (
            <div className="mt-2 rounded border border-amber-200 bg-amber-50 p-2 text-sm text-amber-800">
              {PRODUCT_UNCONFIRMED}
            </div>
          )}
        </Block>
      )}

      <Block title={evidenceTitle(row.kind)}>
        <Evidence evidence={row.evidence} />
      </Block>

      <Block title={isInvoice ? 'Propuesta: mandar la factura' : 'Propuesta'}>
        {isInvoice && <p className="mb-2 text-sm text-gray-700">{INVOICE_CARD_NOTE}</p>}
        <pre className="whitespace-pre-wrap rounded border border-gray-200 bg-gray-50 p-3 text-sm text-gray-800">
          {row.text}
        </pre>
      </Block>

      {(mode === 'edit' || mode === 'edit_confirm') && (
        <div className="mt-3">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            disabled={mode === 'edit_confirm'}
            rows={Math.min(12, Math.max(4, Math.ceil(draft.length / 80)))}
            className="w-full rounded border border-gray-300 p-2 text-sm"
          />
          <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
            <span className={draft.length > row.max_len ? 'text-red-600' : 'text-gray-500'}>
              {fmt(draft.length)} / {fmt(row.max_len)}
            </span>
            {problem && <span className="text-red-600">{problem}</span>}
          </div>
        </div>
      )}

      {error && (
        <div className="mt-3 rounded border border-red-200 bg-red-50 p-2 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="mt-3 flex flex-wrap gap-2">
        {mode === 'idle' && (
          <>
            <Btn tone="go" onClick={() => askConfirm('confirm', 'idle')}>
              Sí
            </Btn>
            {!isInvoice && (
              <Btn
                onClick={() => {
                  setDraft(row.text);
                  setMode('edit');
                }}
              >
                Sí con cambios
              </Btn>
            )}
            <Btn tone="no" onClick={() => setMode('reject')}>
              No
            </Btn>
          </>
        )}
        {/* La confirmación queda a la derecha y "Cancelar" donde estaba el botón anterior:
            el segundo clic de un doble clic no cae sobre "Sí, enviar". */}
        {mode === 'confirm' && (
          <>
            <Btn onClick={() => setMode('idle')}>Cancelar</Btn>
            <span className="ml-auto">
              <Btn tone="go" onClick={() => confirmed(() => void approve())}>
                ¿Seguro? Es irreversible. Sí, enviar
              </Btn>
            </span>
          </>
        )}
        {mode === 'edit' && (
          <>
            <Btn tone="go" disabled={!!problem} onClick={() => askConfirm('edit_confirm', 'edit')}>
              Enviar así
            </Btn>
            <Btn onClick={() => setMode('idle')}>Cancelar</Btn>
          </>
        )}
        {mode === 'edit_confirm' && (
          <>
            <Btn onClick={() => setMode('edit')}>Volver a editar</Btn>
            <span className="ml-auto">
              <Btn
                tone="go"
                disabled={!!problem}
                onClick={() => confirmed(() => void approve(draft))}
              >
                ¿Seguro? Es irreversible. Sí, enviar
              </Btn>
            </span>
          </>
        )}
        {mode === 'reject' && (
          <>
            <span className="self-center text-sm text-gray-600">¿Por qué no?</span>
            {REJECT_OPTIONS.map((o) => (
              <Btn key={o.value} tone="no" onClick={() => void reject(o.value)}>
                {o.label}
              </Btn>
            ))}
            <Btn onClick={() => setMode('idle')}>Cancelar</Btn>
          </>
        )}
        {busy && <span className="text-sm text-gray-500">Procesando…</span>}
      </div>
    </article>
  );
}

function Block({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mt-3">
      <div className="mb-1 text-xs font-semibold text-gray-500">{title}</div>
      {children}
    </div>
  );
}

function BuyerSide({ kind, ctx }: { kind: string; ctx: Record<string, unknown> }) {
  if (kind === 'send_email' || kind === 'reply_email') {
    const lines = mailSideLines(ctx);
    return lines.length ? (
      <ul className="space-y-0.5 rounded bg-gray-50 p-2 text-sm">
        {lines.map((l, i) => (
          <li key={i}>{l}</li>
        ))}
      </ul>
    ) : (
      <p className="text-sm text-gray-500">No se guardó el detalle del correo.</p>
    );
  }
  if (kind === 'answer_question') {
    const q = str(ctx.question_text);
    return q ? (
      <div className="rounded bg-gray-50 p-2 text-sm">
        <div className="mb-0.5 text-xs text-gray-500">
          Comprador · {fmtShortDateTime(str(ctx.question_date))}
        </div>
        <div className="whitespace-pre-wrap">{q}</div>
      </div>
    ) : (
      <p className="text-sm text-gray-500">No se guardó el texto de la pregunta.</p>
    );
  }
  const msgs = Array.isArray(ctx.messages)
    ? (ctx.messages as Array<{ who?: unknown; date?: unknown; text?: unknown }>)
    : [];
  if (msgs.length === 0)
    return <p className="text-sm text-gray-500">No se guardaron los mensajes.</p>;
  return (
    <ul className="space-y-1">
      {msgs.map((m, i) => {
        const ours = m.who === 'nosotros';
        return (
          <li
            key={i}
            className={`rounded p-2 text-sm ${ours ? 'bg-blue-50' : 'border border-gray-200 bg-white'}`}
          >
            <div className="mb-0.5 text-xs text-gray-500">
              {ours ? 'Nosotros' : 'Comprador'} · {fmtShortDateTime(str(m.date))}
            </div>
            <div className="whitespace-pre-wrap">{str(m.text) ?? ''}</div>
          </li>
        );
      })}
    </ul>
  );
}

function PreviousThread({ entries }: { entries: ReturnType<typeof previousQa> }) {
  return (
    <ul className="space-y-1">
      {entries.map((e, i) => (
        <li key={i} className="rounded border border-gray-200 bg-white p-2 text-sm">
          <div className="mb-0.5 text-xs text-gray-500">
            Comprador{e.date ? ` · ${fmtShortDateTime(e.date)}` : ''}
          </div>
          <div className="whitespace-pre-wrap">{e.question}</div>
          <div className="mt-1 whitespace-pre-wrap rounded bg-blue-50 p-1.5 text-gray-700">
            {e.answer ? (
              <>
                <span className="text-xs text-gray-500">Nuestra respuesta: </span>
                {e.answer}
              </>
            ) : (
              <span className="text-xs text-gray-500">Todavía sin responder.</span>
            )}
          </div>
        </li>
      ))}
    </ul>
  );
}

function Product({ ctx }: { ctx: Record<string, unknown> }) {
  const title = str(ctx.item_title);
  const url = str(ctx.item_url);
  if (title) {
    return url && /^https?:\/\//.test(url) ? (
      <a href={url} target="_blank" rel="noreferrer" className="text-sm text-blue-700 underline">
        {title}
      </a>
    ) : (
      <p className="text-sm">{title}</p>
    );
  }
  const items = Array.isArray(ctx.items)
    ? (ctx.items as Array<{ title?: unknown; quantity?: unknown }>)
    : [];
  const named = items.filter((it) => str(it.title));
  if (named.length === 0) return <p className="text-sm text-gray-500">Sin datos del producto.</p>;
  return (
    <ul className="text-sm">
      {named.map((it, i) => (
        <li key={i}>
          {str(it.title)}
          {Number(it.quantity) > 1 ? ` (${fmt(Number(it.quantity))} unidades)` : ''}
        </li>
      ))}
    </ul>
  );
}

function Evidence({ evidence }: { evidence: Record<string, unknown> | null }) {
  const ev = evidence ?? {};
  const verified = Array.isArray(ev.verified)
    ? (ev.verified as unknown[]).map(str).filter((x): x is string => !!x)
    : [];
  if (verified.length > 0) {
    return (
      <ul className="list-disc pl-5 text-sm">
        {verified.map((v, i) => (
          <li key={i}>{v}</li>
        ))}
      </ul>
    );
  }
  const at = str(ev.verified_live_at);
  if (at && ev.live_status === 'UNANSWERED') {
    const d = new Date(at);
    const hhmm = Number.isNaN(d.getTime())
      ? ''
      : ` a las ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    return <p className="text-sm">Verificado en vivo{hhmm}: sin responder.</p>;
  }
  return <p className="text-sm text-gray-500">No hay detalle de lo que verificó.</p>;
}

function Btn({
  children,
  onClick,
  tone,
  disabled,
}: {
  children: React.ReactNode;
  onClick: () => void;
  tone?: 'go' | 'no';
  disabled?: boolean;
}) {
  const cls =
    tone === 'go'
      ? 'bg-emerald-600 text-white hover:bg-emerald-700'
      : tone === 'no'
        ? 'border border-red-300 text-red-700 hover:bg-red-50'
        : 'border border-gray-300 hover:bg-gray-50';
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`rounded px-3 py-1.5 text-sm font-medium disabled:opacity-50 ${cls}`}
    >
      {children}
    </button>
  );
}
